import type { ChatMessage } from "./ChatBot.types";

export type ChatUiLocale = "en" | "sr-latn" | "sr-cyrl";

const CYRILLIC = /[\u0400-\u04FF]/;
const SR_LATIN_DIACRITICS = /[ćčđšžĆČĐŠŽ]/;

/** ASCII Serbian hints — same idea as chatbot `progress-ui-locale.util`. */
const SR_ASCII_HINT =
  /\b(potrebna|potreban|trebaju|treba|hvala|molim|pozdrav|zdravo|želim|zelim|hoću|hocu|hoci|pretraga|pronadji|pronaci|pronađi|pronaći|dokumenata|dokumenta|dokumente|dokumenti|zadatak|zadaci|zadatke|izvestaj|izvestaje|izveštaj|izveštaje|tabelu|tabela|preuzimanje|nedelji|nedelje|nedelja|nedelju|marta|martu|indeksirana|indeksiranu|indeksiran|sva|svu|prva|prvi|prvu|tacnije|tačnije|razumem|trazim|tražim|trazis|tražiš|ćao|cao|format|cetu|četu|želite|zelite|mi\s+treba|da\s+da|izvoz|csv\s+mi)\b/i;

/**
 * Infer fixed UI chrome locale from the last few user messages (mirrors backend
 * `inferProgressUiLocale` rules).
 */
export function inferChatUiLocale(
  messages: ChatMessage[],
  maxUserTurns = 5
): ChatUiLocale {
  const texts: string[] = [];
  for (
    let i = messages.length - 1;
    i >= 0 && texts.length < maxUserTurns;
    i--
  ) {
    const m = messages[i];
    if (m.role !== "user") continue;
    const t = m.content?.trim();
    if (t) texts.push(t);
  }
  if (texts.length === 0) return "en";
  for (const text of texts) {
    if (CYRILLIC.test(text)) return "sr-cyrl";
  }
  for (const text of texts) {
    if (SR_LATIN_DIACRITICS.test(text)) return "sr-latn";
  }
  if (SR_ASCII_HINT.test(texts.join(" "))) return "sr-latn";
  return "en";
}

function messagesCountEn(n: number): string {
  return `${n} message${n !== 1 ? "s" : ""}`;
}

function messagesCountSrLatn(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} poruka`;
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14))
    return `${n} poruke`;
  return `${n} poruka`;
}

function messagesCountSrCyrl(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} порука`;
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14))
    return `${n} поруке`;
  return `${n} порука`;
}

export type ChatUiStrings = {
  send: string;
  stop: string;
  placeholder: string;
  working: string;
  messagesCount: (n: number) => string;
  emptyTitle: string;
  emptyLine1: string;
  emptyLine2: string;
  emptyBilingualNote: string;
  exportInProgressFallback: string;
  fraudControlUploadButton: string;
  fraudControlProcessing: string;
  fraudControlError: string;
  fraudControlModalTitle: string;
  fraudControlModalDescription: string;
  fraudControlModalUpload: string;
  fraudControlModalClose: string;
};

export const chatUiStrings: Record<ChatUiLocale, ChatUiStrings> = {
  en: {
    send: "Send",
    stop: "Stop",
    placeholder: "Ask about documents in Aurora DMS...",
    working: "Working…",
    messagesCount: messagesCountEn,
    emptyTitle: "👋 Welcome to Aurora AI Assistant",
    emptyLine1:
      "I can help you search for documents and tasks in Aurora, or check which documents from your list are missing — use the upload button to run a fraud control check.",
    emptyLine2:
      'Try asking: "Show me documents from last month" or "Find documents for John Smith"',
    emptyBilingualNote: "Slobodno mi se možeš obratiti i na srpskom jeziku.",
    exportInProgressFallback: "Export is in progress, please wait…",
    fraudControlUploadButton: "Upload CSV for fraud control",
    fraudControlProcessing: "Checking list…",
    fraudControlError: "Upload failed. Please try again.",
    fraudControlModalTitle: "Fraud Control",
    fraudControlModalDescription: "Upload a CSV file with three columns: MSISDN, JMBG, and Sign Date. The system will return a downloadable list of entries not found in Aurora.",
    fraudControlModalUpload: "Upload",
    fraudControlModalClose: "Close",
  },
  "sr-latn": {
    send: "Pošalji",
    stop: "Zaustavi",
    placeholder: "Pitaj o dokumentima u Aurora DMS…",
    working: "Radim…",
    messagesCount: messagesCountSrLatn,
    emptyTitle: "👋 Dobrodošli u Aurora AI asistenta",
    emptyLine1:
      "Mogu da ti pomognem da pretražuješ dokumente i zadatke u Aurori, ili da proveriš koji dokumenti nedostaju — koristi dugme za otpremanje za fraud control proveru.",
    emptyLine2:
      "Pokušaj: „Pokaži mi dokumente od prošlog meseca” ili „Nađi dokumente za Jovana Jovanovića”",
    emptyBilingualNote: "Možeš pisati i na engleskom.",
    exportInProgressFallback: "Izvoz je u toku, sačekaj…",
    fraudControlUploadButton: "Otpremi CSV za fraud control",
    fraudControlProcessing: "Provera liste…",
    fraudControlError: "Otpremanje nije uspelo. Pokušajte ponovo.",
    fraudControlModalTitle: "Fraud Control",
    fraudControlModalDescription: "Otpremi CSV fajl sa tri kolone: MSISDN, JMBG i datum potpisivanja. Sistem će vratiti listu unosa koji nisu pronađeni u Aurori.",
    fraudControlModalUpload: "Otpremi",
    fraudControlModalClose: "Zatvori",
  },
  "sr-cyrl": {
    send: "Пошаљи",
    stop: "Заустави",
    placeholder: "Питај о документима у Aurora DMS…",
    working: "Радим…",
    messagesCount: messagesCountSrCyrl,
    emptyTitle: "👋 Добродошли у Aurora AI асистента",
    emptyLine1:
      "Могу да ти помогнем да претражујеш документе и задатке у Аурори, или да провериш који документи недостају — користи дугме за отпремање за fraud control проверу.",
    emptyLine2:
      "Покушај: „Покажи ми документе од прошлог месеца” или „Нађи документе за Јована Јовановића”",
    emptyBilingualNote: "Можете писати и на енглеском.",
    exportInProgressFallback: "Извоз је у току, сачекај…",
    fraudControlUploadButton: "Отпреми CSV за fraud control",
    fraudControlProcessing: "Провера листе…",
    fraudControlError: "Отпремање није успело. Покушајте поново.",
    fraudControlModalTitle: "Fraud Control",
    fraudControlModalDescription: "Отпреми CSV фајл са три колоне: MSISDN, JMBG и датум потписивања. Систем ће вратити листу уноса који нису пронађени у Аурори.",
    fraudControlModalUpload: "Отпреми",
    fraudControlModalClose: "Затвори",
  },
};
